import React, { useState } from "react";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEFRAMES = [
  "Within 24 Hours",
  "Within 7 Days",
  "Within 30 Days",
  "Over 30 Days",
];
const STATES = ["Florida", "Texas"];
const INSURED = ["Yes", "No", "Unsure"];

const initialForm = {
  full_name: "",
  phone: "",
  state: "",
  accident_timeframe: "",
  other_driver_insured: "",
  description: "",
  consent_to_contact: false,
};

const inputClass =
  "w-full bg-[#0F2240] border border-[#B89968]/25 text-white placeholder:text-[#64748B] focus:border-[#B89968] focus:ring-0 focus-visible:ring-0 rounded-none px-4 py-3 h-auto text-base transition-colors";

const selectTriggerClass =
  "w-full bg-[#0F2240] border border-[#B89968]/25 text-white data-[placeholder]:text-[#64748B] focus:border-[#B89968] focus:ring-0 focus:ring-offset-0 rounded-none px-4 py-3 h-auto text-base hover:border-[#B89968]/60 transition-colors";

const labelClass =
  "block text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] mb-2 font-medium";

export default function LeadForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (val) =>
    setForm((f) => ({ ...f, [key]: typeof val === "string" ? val : val.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      return "Please enter a valid phone number.";
    if (!form.state) return "Please select your state.";
    if (!form.accident_timeframe) return "Please tell us when the accident happened.";
    if (!form.other_driver_insured) return "Please select whether the other driver was insured.";
    if (!form.consent_to_contact)
      return "Please authorize us to contact you by phone to continue.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="relative bg-[#0F2240] py-20 md:py-28 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#B89968]" />
              <span className="text-[#B89968] text-xs uppercase tracking-[0.32em]">
                Confidential Case Review
              </span>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              Tell us what happened.
              <br />
              <span className="italic gold-text">We'll take it from here.</span>
            </h2>
            <p className="mt-6 text-[#CBD5E1] text-lg max-w-md leading-relaxed">
              Takes less than <span className="text-white font-medium">60 seconds</span>. A
              member of our team will reach out personally — usually within a few
              hours.
            </p>

            <ul className="mt-10 space-y-4 text-[#E2E8F0]">
              {[
                "No fee unless we win",
                "100% confidential",
                "Direct access to your attorney",
                "Florida & Texas representation",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#B89968]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right form card */}
          <div className="lg:col-span-7">
            <div
              data-testid="lead-form-card"
              className="relative bg-[#15294C] border border-[#B89968]/20 p-6 md:p-10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
              style={{ borderTop: "3px solid #B89968" }}
            >
              {success ? (
                <SuccessBlock onReset={() => setSuccess(false)} />
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name" htmlFor="full_name">
                      <Input
                        id="full_name"
                        data-testid="lead-form-name-input"
                        value={form.full_name}
                        onChange={update("full_name")}
                        placeholder="John Smith"
                        className={inputClass}
                        autoComplete="name"
                      />
                    </Field>
                    <Field label="Phone Number" htmlFor="phone">
                      <Input
                        id="phone"
                        data-testid="lead-form-phone-input"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(555) 123-4567"
                        className={inputClass}
                        type="tel"
                        autoComplete="tel"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="State">
                      <Select
                        value={form.state}
                        onValueChange={update("state")}
                      >
                        <SelectTrigger
                          data-testid="lead-form-state-select"
                          className={selectTriggerClass}
                        >
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#15294C] border-[#B89968]/30 text-white rounded-none">
                          {STATES.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`state-option-${s.toLowerCase()}`}
                              className="focus:bg-[#B89968]/15 focus:text-white cursor-pointer"
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="How Long Ago Was the Accident?">
                      <Select
                        value={form.accident_timeframe}
                        onValueChange={update("accident_timeframe")}
                      >
                        <SelectTrigger
                          data-testid="lead-form-timeframe-select"
                          className={selectTriggerClass}
                        >
                          <SelectValue placeholder="Select timeframe" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#15294C] border-[#B89968]/30 text-white rounded-none">
                          {TIMEFRAMES.map((t) => (
                            <SelectItem
                              key={t}
                              value={t}
                              className="focus:bg-[#B89968]/15 focus:text-white cursor-pointer"
                            >
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Was the Other Driver Insured?">
                    <Select
                      value={form.other_driver_insured}
                      onValueChange={update("other_driver_insured")}
                    >
                      <SelectTrigger
                        data-testid="lead-form-insured-select"
                        className={selectTriggerClass}
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#15294C] border-[#B89968]/30 text-white rounded-none">
                        {INSURED.map((i) => (
                          <SelectItem
                            key={i}
                            value={i}
                            className="focus:bg-[#B89968]/15 focus:text-white cursor-pointer"
                          >
                            {i}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field
                    label={
                      <>
                        Brief Description of the Accident{" "}
                        <span className="normal-case tracking-normal text-[#64748B] text-[10px] ml-1">
                          (optional)
                        </span>
                      </>
                    }
                    htmlFor="description"
                  >
                    <Textarea
                      id="description"
                      data-testid="lead-form-description-textarea"
                      value={form.description}
                      onChange={update("description")}
                      placeholder="Anything you'd like us to know — feel free to skip."
                      className={`${inputClass} min-h-[100px] resize-none`}
                      rows={4}
                    />
                  </Field>

                  {error && (
                    <div
                      data-testid="lead-form-error"
                      className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#FCA5A5] text-sm px-4 py-3"
                    >
                      {error}
                    </div>
                  )}

                  <label
                    htmlFor="consent_to_contact"
                    className="flex items-start gap-3 cursor-pointer group select-none"
                  >
                    <input
                      id="consent_to_contact"
                      type="checkbox"
                      data-testid="lead-form-consent-checkbox"
                      checked={form.consent_to_contact}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          consent_to_contact: e.target.checked,
                        }))
                      }
                      className="mt-1 shrink-0 h-5 w-5 rounded-none border border-[#B89968]/50 bg-[#0F2240] accent-[#B89968] cursor-pointer"
                    />
                    <span className="text-[13px] leading-relaxed text-[#CBD5E1]">
                      I authorize Newman Injury Law to contact me by phone at
                      the number provided to discuss my case. I understand this
                      is not a request for legal advice and does not create an
                      attorney-client relationship.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-form-submit-button"
                    className="w-full bg-[#B89968] hover:bg-[#CFB079] disabled:opacity-60 disabled:cursor-not-allowed text-[#0F2240] py-4 md:py-5 font-bold uppercase tracking-[0.18em] text-sm md:text-base transition-colors duration-300"
                  >
                    {submitting ? "Sending..." : "Speak With Our Team"}
                  </button>

                  <p className="text-center text-xs md:text-sm text-[#94A3B8] tracking-wide">
                    No fee unless we win. Fast response. 100% confidential.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelClass}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SuccessBlock({ onReset }) {
  return (
    <div data-testid="lead-form-success" className="py-12 text-center">
      <div className="mx-auto w-16 h-16 flex items-center justify-center border-2 border-[#B89968] rounded-full mb-6">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#B89968"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3 className="font-serif-display text-2xl md:text-3xl text-white mb-4">
        Thank you.
      </h3>
      <p className="text-[#CBD5E1] text-lg max-w-md mx-auto leading-relaxed">
        Someone from the team at Newman Personal Injury will contact you shortly.
      </p>
      <button
        onClick={onReset}
        data-testid="lead-form-reset-button"
        className="mt-8 text-[#B89968] uppercase tracking-[0.22em] text-xs hover:text-[#CFB079] transition-colors"
      >
        Submit another inquiry
      </button>
    </div>
  );
}
